import React, { useRef, useState } from "react"
import { useRouter } from "next/router"
import { mutate } from "swr"
import queryString from "query-string"
import * as dayjs from "dayjs"
import {
  Order,
  OrderApi,
  ResponseEnterpriseGetListMeta,
} from "../../libs/api/backend/src"
import { createConfiguration } from "../../libs/api/openApi"
import { Minus, Plus } from "lucide-react"
import { UrlOrderFindListAPI } from "../../libs/api/url"
import { useMainEnterpriseId } from "../../hooks/enterprise"
import { formatPriceKZT } from "@malond/utils"
import { castOrderTypeToText } from "../../libs/helpers/order"
import { orderTypeOrderAdd } from "../../libs/validation/vars"
import { useShopId } from "../../hooks/shop"
import { useLimit, useOffset } from "../../hooks/pagination"
import { Pagination } from "../Navs/Pagination"
import {
  getCurrentPageFromMeta,
  getMetaFromPagination,
} from "../../libs/helpers/pagination"
import { useShopSalePointId } from "../../hooks/shopSalePoint"

function OrderTable({
  orders,
  meta,
}: {
  orders: Array<Order>
  meta?: ResponseEnterpriseGetListMeta
}) {
  const clientAPI = new OrderApi(createConfiguration())

  const router = useRouter()

  const enterpriseId = useMainEnterpriseId()
  const shopId = useShopId()
  const shopSalePointId = useShopSalePointId()
  const limit = useLimit()
  const offset = useOffset()

  const [isOpenEdit, setIsOpenEdit] = useState(false)
  const [currentItem, setCurrentItem] = useState<Order | undefined>()
  const cancelButtonRef = useRef(null)

  const openCloseModal = (order?: Order) => {
    setIsOpenEdit(!isOpenEdit)
    setCurrentItem(order)
  }

  const onSubmit = async (values: Order, { setSubmitting }: any) => {
    setSubmitting(true)

    try {
      const { success } = await clientAPI.orderUpdateOne({
        enterpriseId: enterpriseId,
        orderId: values.id,
        requestOrderCreateOne: {
          typeOrder: values.typeOrder,
          reason: values.reason,
          sum: values.sum,
          sourceType: values.sourceType,
          shopId: values.shopId,
          shopSalePointId: values.shopSalePointId,
          shopPaymentPointId: values.shopPaymentPointId,
        },
      })

      if (!success) {
        return
      }

      openCloseModal()

      await mutate(
        UrlOrderFindListAPI(enterpriseId, { shopId, limit, offset }),
        undefined,
        true
      )

      if (shopId !== values.shopId) {
        await mutate(
          UrlOrderFindListAPI(enterpriseId, {
            shopId: values.shopId,
            limit,
            offset,
          }),
          undefined,
          true
        )
      }

      if (currentItem && values.shopId !== currentItem.shopId) {
        await mutate(
          UrlOrderFindListAPI(enterpriseId, {
            shopId: currentItem.shopId,
            limit,
            offset,
          }),
          undefined,
          true
        )
      }
    } catch (error) {
      console.error(error)
    } finally {
      setSubmitting(false)
    }
  }

  const onChangePagination = async (page: number, pageSize: number) => {
    const { limit, offset } = getMetaFromPagination(page, pageSize)

    await router.push(
      `/e/${enterpriseId}/${shopId}/${shopSalePointId}/orders?${queryString.stringify(
        {
          shopId: shopId,
          limit: limit,
          offset: offset,
        },
        { skipNull: true }
      )}`
    )
  }

  return (
    <div className="mt-6 overflow-hidden rounded-lg border border-border bg-card shadow-xs">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border bg-muted/50 text-xs font-semibold tracking-wider text-muted-foreground uppercase">
            <tr>
              <th scope="col" className="px-4 py-3 whitespace-nowrap sm:px-6">
                Дата и время
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-right whitespace-nowrap"
              >
                № ордера
              </th>
              <th scope="col" className="px-4 py-3 whitespace-nowrap sm:px-6">
                Тип
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-right whitespace-nowrap"
              >
                Сумма
              </th>
              <th scope="col" className="px-4 py-3 whitespace-nowrap">
                Описание
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-border">
            {orders && orders.length ? (
              orders.map((order) => {
                const isAdd = order.typeOrder === orderTypeOrderAdd
                return (
                  <tr
                    key={order.id}
                    className="transition-colors hover:bg-muted/30"
                  >
                    <td className="px-4 py-3 text-sm whitespace-nowrap sm:px-6">
                      <span className="font-medium text-foreground">
                        {dayjs
                          .unix(order.createdAt as number)
                          .format("DD.MM.YYYY")}
                      </span>
                      &nbsp;&nbsp;
                      <span className="text-muted-foreground">
                        {dayjs.unix(order.createdAt as number).format("HH:mm")}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-right text-sm whitespace-nowrap">
                      <span className="font-mono font-medium text-foreground">
                        {order.orderId}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-sm whitespace-nowrap sm:px-6">
                      <div className="inline-flex items-center gap-1.5">
                        {isAdd ? (
                          <span className="inline-flex items-center justify-center rounded-full bg-emerald-500/15 p-1 text-emerald-600 dark:text-emerald-400">
                            <Plus className="size-3" />
                          </span>
                        ) : (
                          <span className="inline-flex items-center justify-center rounded-full bg-destructive/15 p-1 text-destructive">
                            <Minus className="size-3" />
                          </span>
                        )}
                        <span className="font-medium text-foreground">
                          {castOrderTypeToText(order.typeOrder)}
                        </span>
                      </div>
                    </td>

                    <td className="px-4 py-3 text-right text-sm font-semibold whitespace-nowrap">
                      <span
                        className={
                          isAdd
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-foreground"
                        }
                      >
                        {isAdd ? "+" : "-"} {formatPriceKZT(order.sum)}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-sm whitespace-nowrap text-muted-foreground">
                      {order.reason || "—"}
                    </td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-8 text-center text-sm whitespace-nowrap text-muted-foreground"
                >
                  Ордеров пока нет
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {meta && meta.total ? (
        <Pagination
          page={getCurrentPageFromMeta(meta)}
          pageSize={Number(meta.limit) || 20}
          total={Number(meta.total) || 0}
          onChange={onChangePagination}
        />
      ) : null}
    </div>
  )
}

export default OrderTable
